
import React, { useEffect, useRef } from 'react';

import {
    getVideoDeviceIds,
    getVideoDeviceLabels,
    setVideoInputDeviceAndUpdateSettings
} from '../../../../base/devices';
import { openDialog } from '../../../../base/dialog';
import { InlineDialog } from '../../../../base/react';
import { connect } from '../../../../base/redux';
import { getCurrentCameraDeviceId, getUserSelectedCameraDeviceId } from '../../../../base/settings';
import { VirtualBackgroundDialog } from '../../../../virtual-background/components/web';
import { setVideoSettingsVisibility } from '../../../actions';
import { getVideoSettingsVisibility } from '../../../functions';

import VideoSettingsContent, { type Props as VideoSettingsProps } from './VideoSettingsContent';


type Props = VideoSettingsProps & {

    /**
     * Component children (the Video button).
     */
    children: React$Node,

    /**
     * Flag controlling the visibility of the popup.
     */
    isOpen: boolean,

    /**
     * Callback executed when the popup closes.
     */
    onClose: Function,

    /**
     * Callback executed when opening the virtual background dialog.
     */
    onOpenVirtualBackground: Function,
}

/**
 * Popup with a preview of all the video devices.
 *
 * @returns {ReactElement}
 */
function VideoSettingsPopup({
    currentCameraDeviceId,
    children,
    deviceLabels,
    isOpen,
    onClose,
    setVideoInputDevice,
    videoDeviceIds,
    onOpenVirtualBackground
}: Props) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const targetDocument = containerRef.current?.ownerDocument || document;
        const targetWindow = targetDocument.defaultView || window;
        const pipWindow = window.documentPictureInPicture?.window;
        const pipDocument = pipWindow?.document;

        let lastWidth = targetWindow.innerWidth;

        const handleResize = () => {
            if (targetWindow.innerWidth > lastWidth) {
                onClose();
            }
            lastWidth = targetWindow.innerWidth;
        };

        const handleClickOutside = event => {
            if (!containerRef.current) {
                return;
            }

            if (containerRef.current.contains(event.target)) {
                return;
            }

            onClose();
        };

        const docs = new Set();
        const wins = new Set();

        docs.add(targetDocument);
        wins.add(targetWindow);

        if (document) {
            docs.add(document);
        }
        if (window) {
            wins.add(window);
        }

        if (pipDocument) {
            docs.add(pipDocument);
        }
        if (pipWindow) {
            wins.add(pipWindow);
        }

        wins.forEach(win => {
            win.addEventListener('resize', handleResize);
        });

        docs.forEach(doc => {
            doc.addEventListener('click', handleClickOutside, true);
        });

        return () => {
            wins.forEach(win => {
                win.removeEventListener('resize', handleResize);
            });
            docs.forEach(doc => {
                doc.removeEventListener('click', handleClickOutside, true);
            });
        };
    }, [ isOpen, onClose ]);

    return (
        <div
            className = 'video-preview'
            ref = { containerRef }>
            <InlineDialog
                content = { <VideoSettingsContent
                    currentCameraDeviceId = { currentCameraDeviceId }
                    deviceLabels = { deviceLabels }
                    onOpenVirtualBackground = { onOpenVirtualBackground }
                    setVideoInputDevice = { setVideoInputDevice }
                    toggleVideoSettings = { onClose }
                    videoDeviceIds = { videoDeviceIds } /> }
                isOpen = { isOpen }
                onClose = { onClose }
                placement = 'top'>
                {children}
            </InlineDialog>
        </div>
    );
}

/**
 * Maps (parts of) the redux state to the associated {@code VideoSettingsPopup}'s
 * props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    // Carried alongside the ids so an entry whose preview track could not be created can still name the
    // camera it selects - `MHLocalTrack.getTrackLabel()` is not available without a track.
    const deviceLabels = getVideoDeviceLabels(state);

    return {
        currentCameraDeviceId: getCurrentCameraDeviceId(state)
            || getUserSelectedCameraDeviceId(state)
            || state['features/base/settings']?.userSelectedCameraDeviceId
            || state['features/base/settings']?.cameraDeviceId,
        deviceLabels,
        isOpen: getVideoSettingsVisibility(state),
        videoDeviceIds: getVideoDeviceIds(state)
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onClose: () => dispatch(setVideoSettingsVisibility(false)),
        setVideoInputDevice: deviceId => dispatch(setVideoInputDeviceAndUpdateSettings(deviceId)),
        onOpenVirtualBackground: () => {
            // dispatch(toggleVideoSettings());
            dispatch(openDialog(VirtualBackgroundDialog, undefined, true));
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VideoSettingsPopup);
